import { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate } from "react-router-dom";
import axios from "axios";
import { Buildings, Users, CurrencyEur, ChartBar, List, X, Clock, Bell, CalendarBlank, Gear, Table, SignOut, Car, Tag, Airplane, FileText, Package, CaretUpDown, NotePencil, Storefront, ClipboardText } from "@phosphor-icons/react";
import Dashboard from "@/pages/Dashboard";
import Medewerkers from "@/pages/Medewerkers";
import Filialen from "@/pages/Filialen";
import Kosten from "@/pages/Kosten";
import Aanwezigheid from "@/pages/Aanwezigheid";
import Maandoverzicht from "@/pages/Maandoverzicht";
import Weekoverzicht from "@/pages/Weekoverzicht";
import Instellingen from "@/pages/Instellingen";
import Inklokken from "@/pages/Inklokken";
import Wagenpark from "@/components/WagenparkTab";
import RitPage from "@/pages/RitPage";
import DriverPage from "@/pages/DriverPage";
import Kortingscalculator from "@/pages/Kortingscalculator";
import UrenInvoer from "@/pages/UrenInvoer";
import Verlof from "@/pages/Verlof";
import Planning from "@/pages/Planning";
import Contracten from "@/pages/Contracten";
import StockBijvullen from "@/pages/StockBijvullen";
import Kassa from "@/pages/Kassa";
import Login from "@/pages/Login";
import Firmas from "@/pages/Firmas";
import Logboek from "@/pages/Logboek";
import CtnOrders from "@/pages/CtnOrders";
import CtnOrderForm from "@/pages/CtnOrderForm";
import CtnOrderDetail from "@/pages/CtnOrderDetail";
import CtnCatalog from "@/pages/CtnCatalog";
import CtnCatalogTransfer from "@/pages/CtnCatalogTransfer";
import CtnReports from "@/pages/CtnReports";
import CtnExport from "@/pages/CtnExport";
import CtnImport from "@/pages/CtnImport";
import CtnDrafts from "@/pages/CtnDrafts";
import CtnRates from "@/pages/CtnRates";
import { CtnNav } from "@/components/ctn/CtnNav";
import { LoonSlot } from "@/components/LoonSlot";
import { SalaryVisibilityProvider } from "@/components/salary/SalaryVisibility";
import { koppelAuthRetry } from "@/lib/authRetry";
import { useCtnAccess } from "@/hooks/useCtnAccess";
import { PageBackButton } from "@/components/PageBackButton";
import { Toaster } from "@/components/ui/sonner";
import { InstallBanner } from "@/components/InstallBanner";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "hr_token";
const FIRMA_KEY = "hr_firma";
const DEVICE_KEY = "hr_device_id";

// Apparaat-id: blijft op dit toestel staan, nodig voor sessie-overzicht + apparaatslot
let deviceId = localStorage.getItem(DEVICE_KEY);
if (!deviceId) {
  deviceId = (window.crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  localStorage.setItem(DEVICE_KEY, deviceId);
}
axios.defaults.headers.common["X-Device-Id"] = deviceId;

// Headers herstellen vóór de eerste API-call (na refresh)
const bootToken = localStorage.getItem(TOKEN_KEY);
if (bootToken) axios.defaults.headers.common.Authorization = `Bearer ${bootToken}`;
const bootFirma = localStorage.getItem(FIRMA_KEY);
if (bootFirma) axios.defaults.headers.common["X-Firma-Id"] = bootFirma;
// Oude PIN-login liet dit achter; niet meer gebruikt en zou firma-scoping omzeilen
localStorage.removeItem("admin_pwd");
koppelAuthRetry(axios);

function setAuthHeaders(token, firmaId) {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
  if (firmaId) axios.defaults.headers.common["X-Firma-Id"] = firmaId;
  else delete axios.defaults.headers.common["X-Firma-Id"];
}

// ── Auth & Permissions Context ─────────────────────────
const AuthContext = createContext(null);
const PermContext = createContext({});
export const useAuth = () => useContext(AuthContext);
export const usePermissions = () => useContext(PermContext);

// ── Firma-kiezer (super-admin) ─────────────────────────
function FirmaKiezer({ onKies, onLogout }) {
  const [firmas, setFirmas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/firmas`);
        setFirmas(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center p-4" data-testid="firma-kiezer">
      <div className="w-full max-w-lg">
        <h1 className="text-2xl font-black tracking-tight mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>Kies een firma</h1>
        <p className="text-sm text-muted-foreground mb-6">Je bent ingelogd als super-admin. Kies met welke firma je wil werken.</p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Laden...</p>
        ) : (
          <div className="space-y-2">
            {firmas.map((f) => (
              <button
                key={f.id}
                onClick={() => onKies(f.id)}
                className="w-full bg-white border border-border rounded-sm p-4 flex items-center gap-3 hover:border-[#002FA7] transition-colors text-left"
                data-testid={`kies-firma-${f.id}`}
              >
                {f.logo_url ? (
                  <img src={`${BACKEND_URL}${f.logo_url}`} alt={f.naam} className="h-12 w-auto max-w-[140px] object-contain shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-sm bg-[#F4F5F7] border border-border flex items-center justify-center shrink-0">
                    <Buildings size={20} className="text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{f.naam}</p>
                  <p className="text-xs text-muted-foreground truncate">{[f.stad, f.kbo && `KBO ${f.kbo}`].filter(Boolean).join(" · ")}</p>
                </div>
              </button>
            ))}
            {firmas.length === 0 && <p className="text-sm text-muted-foreground">Nog geen firma's. Maak er eerst één aan.</p>}
          </div>
        )}
        <button onClick={onLogout} className="mt-6 text-xs text-muted-foreground hover:text-foreground underline" data-testid="kiezer-logout-btn">Uitloggen</button>
      </div>
    </div>
  );
}

// ── Notification Bell ──────────────────────────────────
function NotificationBell() {
  const [meldingen, setMeldingen] = useState([]);
  const [ongelezen, setOngelezen] = useState(0);

  const fetchMeldingen = useCallback(async () => {
    try {
      const [melRes, countRes] = await Promise.all([axios.get(`${API}/meldingen`), axios.get(`${API}/meldingen/ongelezen`)]);
      setMeldingen(melRes.data.slice(0, 10));
      setOngelezen(countRes.data.aantal);
    } catch (e) {}
  }, []);

  useEffect(() => { fetchMeldingen(); const i = setInterval(fetchMeldingen, 15000); return () => clearInterval(i); }, [fetchMeldingen]);

  const markeerAllesGelezen = async () => { try { await axios.put(`${API}/meldingen/alles-gelezen`); fetchMeldingen(); } catch (e) {} };

  const formatTijd = (isoStr) => { if (!isoStr) return ""; return new Date(isoStr).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-sm hover:bg-muted transition-colors" data-testid="notification-bell">
          <Bell size={20} weight={ongelezen > 0 ? "fill" : "bold"} className="text-foreground" />
          {ongelezen > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#E53E3E] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">{ongelezen > 9 ? "9+" : ongelezen}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notification-dropdown">
        <div className="px-3 py-2 border-b border-border flex items-center justify-between">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Meldingen</p>
          {ongelezen > 0 && <button onClick={markeerAllesGelezen} className="text-xs text-[#002FA7] hover:underline font-medium" data-testid="mark-all-read-btn">Alles gelezen</button>}
        </div>
        {meldingen.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">Geen meldingen</div>
        ) : meldingen.map((m) => (
          <DropdownMenuItem key={m.id} className={`flex flex-col items-start gap-1 px-3 py-2.5 cursor-default ${!m.gelezen ? "bg-primary/5" : ""}`} data-testid={`melding-${m.id}`}>
            <div className="flex items-center gap-2 w-full"><Clock size={14} className="text-[#E53E3E] shrink-0" /><p className="text-xs font-medium flex-1 whitespace-normal">{m.bericht}</p></div>
            <p className="text-[10px] text-muted-foreground ml-5">{formatTijd(m.tijdstip)}</p>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Firma-badge + switcher in sidebar ──────────────────
function FirmaBlok({ firma, firmas, isSuperadmin, onKiesFirma }) {
  const naam = firma?.naam || "Geen firma";
  const inner = (
    <div className="flex items-center gap-2 w-full">
      {firma?.logo_url ? (
        <img
          src={`${BACKEND_URL}${firma.logo_url}`}
          alt={naam}
          className="h-9 w-auto max-w-[96px] object-contain shrink-0"
          data-testid="sidebar-firma-logo"
        />
      ) : (
        <div className="w-9 h-9 rounded-sm bg-[#F4F5F7] border border-border flex items-center justify-center shrink-0">
          <Buildings size={16} color="#B45309" data-testid="sidebar-firma-icon" />
        </div>
      )}
      <div className="flex-1 min-w-0 text-left">
        <p className="text-xs font-bold truncate" data-testid="sidebar-firma-naam">{naam}</p>
        <p className="text-[10px] text-muted-foreground truncate">{firma?.stad || "—"}</p>
      </div>
      {isSuperadmin && <CaretUpDown size={14} color="#2563EB" className="shrink-0" data-testid="sidebar-firma-switch-icon" />}
    </div>
  );

  if (!isSuperadmin) {
    return <div className="px-4 py-3 border-b border-border" data-testid="sidebar-firma">{inner}</div>;
  }

  return (
    <div className="px-3 py-2.5 border-b border-border">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-full p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid="firma-switcher-btn">{inner}</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56" data-testid="firma-switcher-menu">
          {firmas.map((f) => (
            <DropdownMenuItem key={f.id} onClick={() => onKiesFirma(f.id)} className="text-xs" data-testid={`switch-firma-${f.id}`}>
              {f.naam}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ── Sidebar ────────────────────────────────────────────
function Sidebar({ mobileOpen, setMobileOpen, user, firma, firmas, onLogout, onKiesFirma }) {
  const rol = user?.rol || "medewerker";
  const permRol = rol === "superadmin" ? "admin" : rol;
  const perms = usePermissions();
  const myPerms = perms[permRol] || {};

  const allLinks = [
    { to: "/", icon: ChartBar, label: "Dashboard", perm: "dashboard", icoonKleur: "#2563EB" },
    { to: "/medewerkers", icon: Users, label: "Medewerkers", perm: "medewerkers", icoonKleur: "#0F766E" },
    { to: "/filialen", icon: Buildings, label: "Filialen", perm: "filialen", icoonKleur: "#B45309" },
    { to: "/aanwezigheid", icon: Clock, label: "Aanwezigheid", perm: "aanwezigheid", icoonKleur: "#15803D" },
    { to: "/uren-invoer", icon: NotePencil, label: "Handmatig", perm: "aanwezigheid", kleur: "#C62828", icoonKleur: "#C62828" },
    { to: "/planning", icon: CalendarBlank, label: "Planning", perm: "planning", icoonKleur: "#7C3AED" },
    { to: "/weekoverzicht", icon: Table, label: "Weekoverzicht", perm: "weekoverzicht", icoonKleur: "#0369A1" },
    { to: "/maandoverzicht", icon: CalendarBlank, label: "Maandoverzicht", perm: "maandoverzicht", icoonKleur: "#4F46E5" },
    { to: "/kosten", icon: CurrencyEur, label: "Kosten", perm: "kosten", vereistLoon: true, icoonKleur: "#15803D" },
    { to: "/wagenpark", icon: Car, label: "Wagenpark", perm: "wagenpark", icoonKleur: "#C2410C" },
    { to: "/kortingscalculator", icon: Tag, label: "Kortingscalculator", perm: "kortingscalculator", icoonKleur: "#BE185D" },
    { to: "/stock-bijvullen", icon: Package, label: "Stock Bijvullen", perm: "stock_bijvullen", icoonKleur: "#0F766E" },
    { to: "/kassa", icon: Storefront, label: "Kassa / Verkocht", perm: "kassa", icoonKleur: "#B45309" },
    { to: "/verlof", icon: Airplane, label: "Verlof", perm: "verlof", icoonKleur: "#0284C7" },
    { to: "/contracten", icon: FileText, label: "Contracten", perm: "contracten", icoonKleur: "#BE123C" },
    { to: "/instellingen", icon: Gear, label: "Instellingen", perm: "instellingen", icoonKleur: "#0E7490" },
  ];

  const links = allLinks.filter((l) => myPerms[l.perm] === true && (!l.vereistLoon || myPerms.lonen_zien === true));
  const mayFirmas = rol === "superadmin" || (rol === "admin" && myPerms.firmas === true);
  const mayCtn = rol === "superadmin" || myPerms.ctn === true;

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" />}
      <aside id="app-sidebar" data-testid="sidebar" data-mobile-open={mobileOpen ? "true" : "false"} className={`fixed top-0 left-0 h-[100dvh] w-60 bg-white border-r border-border z-50 flex flex-col transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="app-logo">
            <img src="/logo192.png" alt="Personeel Register" className="w-9 h-9 rounded-md object-contain shrink-0" data-testid="app-logo-img" />
            <div className="leading-none">
              <h1 className="text-lg font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Personeel</h1>
              <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-muted-foreground mt-0.5">Register</p>
            </div>
          </div>
          <button className="lg:hidden p-1" onClick={() => setMobileOpen(false)} data-testid="close-sidebar-btn" aria-label="Menu sluiten"><X size={20} color="#BE123C" data-testid="sidebar-close-icon" /></button>
        </div>

        <FirmaBlok firma={firma} firmas={firmas} isSuperadmin={rol === "superadmin"} onKiesFirma={onKiesFirma} />

        <nav className="flex-1 min-h-0 py-4 overflow-y-auto" data-testid="sidebar-nav">
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === "/"} style={link.kleur ? { color: link.kleur, fontWeight: 800 } : undefined} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setMobileOpen(false)} data-testid={`nav-${link.label.toLowerCase()}`}>
              <link.icon size={20} weight="bold" color={link.icoonKleur} className="shrink-0" data-testid={`nav-icon-${link.to === "/" ? "dashboard" : link.to.slice(1)}`} />{link.label}
            </NavLink>
          ))}
          {mayFirmas && (
            <NavLink to="/firmas" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setMobileOpen(false)} data-testid="nav-firmas">
              <Buildings size={20} weight="bold" color="#B45309" className="shrink-0" data-testid="nav-icon-firmas" />Firma's
            </NavLink>
          )}
          {rol === "superadmin" && (
            <NavLink to="/logboek" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setMobileOpen(false)} data-testid="nav-logboek">
              <ClipboardText size={20} weight="bold" color="#7C3AED" className="shrink-0" data-testid="nav-icon-logboek" />Logboek
            </NavLink>
          )}
          {mayCtn && <CtnNav mobileOpen={mobileOpen} onNavigate={() => setMobileOpen(false)} />}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-[10px] font-bold">
              {(user?.naam || user?.email || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate" data-testid="sidebar-user-naam">{user?.naam || user?.email}</p>
              <p className="text-[10px] text-muted-foreground capitalize">{rol}</p>
            </div>
            <button onClick={onLogout} className="p-1 rounded-sm hover:bg-muted transition-colors" data-testid="logout-btn" title="Uitloggen">
              <SignOut size={16} color="#BE123C" data-testid="sidebar-logout-icon" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

// ── Automatische logout bij inactiviteit ───────────────
function useAutoLogout(onLogout) {
  const [cfg, setCfg] = useState({ auto_logout_actief: false, auto_logout_minuten: 30 });

  const laad = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/instellingen/sessie`);
      setCfg(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    laad();
    window.addEventListener("sessie-instellingen-gewijzigd", laad);
    return () => window.removeEventListener("sessie-instellingen-gewijzigd", laad);
  }, [laad]);

  useEffect(() => {
    if (!cfg.auto_logout_actief || !cfg.auto_logout_minuten) return;
    const ms = cfg.auto_logout_minuten * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.info(`Automatisch uitgelogd na ${cfg.auto_logout_minuten} minuten inactiviteit`);
        onLogout();
      }, ms);
    };
    const events = ["mousedown", "keydown", "touchstart", "scroll", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [cfg, onLogout]);
}

// ── App Layout ─────────────────────────────────────────
function AppLayout({ user, firma, activeFirmaId, onLogout, onKiesFirma }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [filialen, setFilialen] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [firmas, setFirmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perms, setPerms] = useState({});
  const rol = user?.rol || "medewerker";
  const permRol = rol === "superadmin" ? "admin" : rol;
  const { permissions: ctnPermissions, ready: ctnAccessReady } = useCtnAccess(user?.id, activeFirmaId);
  const effectivePerms = { ...perms, [permRol]: { ...(perms[permRol] || {}), ...ctnPermissions } };
  const myPerms = effectivePerms[permRol];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [filRes, medRes, permRes] = await Promise.all([
        axios.get(`${API}/filialen`),
        axios.get(`${API}/medewerkers`),
        axios.get(`${API}/instellingen/machtigingen`),
      ]);
      setFilialen(filRes.data);
      setMedewerkers(medRes.data);
      setPerms(permRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, activeFirmaId]);

  const laadFirmas = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/firmas`);
      setFirmas(res.data);
      // Opgeslagen firma bestaat niet meer (bv. verwijderd) → opnieuw laten kiezen
      if (activeFirmaId && !res.data.some((f) => f.id === activeFirmaId)) {
        onKiesFirma(null);
      }
    } catch (e) { /* medewerkers mogen dit niet */ }
  }, [activeFirmaId, onKiesFirma]);

  useEffect(() => { laadFirmas(); }, [laadFirmas, activeFirmaId]);

  useAutoLogout(onLogout);

  const can = (perm) => myPerms[perm] === true;
  const huidigeFirma = firmas.find((f) => f.id === activeFirmaId) || firma || null;
  // `role` blijft beschikbaar voor bestaande pagina's (Medewerkers, Verlof, …)
  const authUser = { ...user, role: permRol };

  return (
    <AuthContext.Provider value={authUser}>
      <SalaryVisibilityProvider key={`${user.id}:${activeFirmaId || ""}`}>
      <PermContext.Provider value={effectivePerms}>
        <div className="min-h-screen bg-background">
          <Sidebar
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            user={user}
            firma={huidigeFirma}
            firmas={firmas}
            onLogout={onLogout}
            onKiesFirma={onKiesFirma}
          />
          <div className="lg:ml-60">
            <header className="sticky top-0 z-30 bg-white border-b border-border px-4 py-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 basis-32 items-center gap-2">
                <button onClick={() => setMobileOpen(true)} data-testid="open-sidebar-btn" aria-label="Menu openen" aria-expanded={mobileOpen} aria-controls="app-sidebar" className="h-10 w-10 shrink-0 inline-flex items-center justify-center lg:hidden"><List size={24} weight="bold" /></button>
                <h1 className="min-w-0 truncate text-base font-bold lg:hidden" data-testid="mobile-firma-name" title={huidigeFirma?.naam || "Personeel Register"} style={{ fontFamily: 'Chivo, sans-serif' }}>{huidigeFirma?.naam || "Personeel Register"}</h1>
              </div>
              <div className="flex shrink-0 items-center gap-2 ml-auto">
                <LoonSlot />
                {(permRol === "admin" || permRol === "manager") && <NotificationBell />}
              </div>
            </header>
            <main className="p-4 sm:p-6 lg:p-8">
              <PageBackButton />
              {loading || !ctnAccessReady ? (
                <div className="flex items-center justify-center h-64" data-testid="app-loading">
                  <div className="text-sm text-muted-foreground">Laden...</div>
                </div>
              ) : (
              <Routes>
                <Route path="/" element={<Dashboard filialen={filialen} medewerkers={medewerkers} loading={loading} />} />
                {can("medewerkers") && <Route path="/medewerkers" element={<Medewerkers medewerkers={medewerkers} filialen={filialen} onRefresh={fetchData} loading={loading} />} />}
                {can("filialen") && <Route path="/filialen" element={<Filialen filialen={filialen} medewerkers={medewerkers} onRefresh={fetchData} loading={loading} />} />}
                {can("aanwezigheid") && <Route path="/aanwezigheid" element={<Aanwezigheid />} />}
                {can("aanwezigheid") && <Route path="/uren-invoer" element={<UrenInvoer />} />}
                {can("planning") && <Route path="/planning" element={<Planning />} />}
                {can("weekoverzicht") && <Route path="/weekoverzicht" element={<Weekoverzicht />} />}
                {can("maandoverzicht") && <Route path="/maandoverzicht" element={<Maandoverzicht />} />}
                {can("kosten") && can("lonen_zien") && <Route path="/kosten" element={<Kosten />} />}
                {can("wagenpark") && <Route path="/wagenpark" element={<Wagenpark />} />}
                {can("kortingscalculator") && <Route path="/kortingscalculator" element={<Kortingscalculator key={activeFirmaId} />} />}
                {can("stock_bijvullen") && <Route path="/stock-bijvullen" element={<StockBijvullen />} />}
                {can("kassa") && <Route path="/kassa" element={<Kassa />} />}
                {(can("ctn") || rol === "superadmin") && <>
                  <Route path="/ctn" element={<Navigate to="/ctn/bestellingen" replace />} />
                  <Route path="/ctn/bestellingen" element={<CtnOrders key={activeFirmaId} />} />
                  <Route path="/ctn/bestellingen/nieuw" element={<CtnOrderForm key={`${activeFirmaId}-new`} />} />
                  <Route path="/ctn/bestellingen/:id" element={<CtnOrderDetail key={activeFirmaId} />} />
                  <Route path="/ctn/bestellingen/:id/bewerken" element={<CtnOrderForm key={`${activeFirmaId}-edit`} />} />
                  <Route path="/ctn/leveranciers" element={<CtnCatalog key={`${activeFirmaId}-suppliers`} kind="leveranciers" />} />
                  <Route path="/ctn/categorieen" element={<CtnCatalog key={`${activeFirmaId}-categories`} kind="categorieen" />} />
                  <Route path="/ctn/overzetten" element={<CtnCatalogTransfer key={activeFirmaId} />} />
                  <Route path="/ctn/rapporten" element={<CtnReports key={activeFirmaId} />} />
                  <Route path="/ctn/export" element={<CtnExport key={activeFirmaId} />} />
                  <Route path="/ctn/import" element={<CtnImport key={activeFirmaId} />} />
                  <Route path="/ctn/concepten" element={<CtnDrafts key={activeFirmaId} />} />
                  <Route path="/ctn/koersen" element={<CtnRates key={activeFirmaId} />} />
                </>}
                {can("verlof") && <Route path="/verlof" element={<Verlof />} />}
                {can("contracten") && <Route path="/contracten" element={<Contracten />} />}
                {can("instellingen") && <Route path="/instellingen" element={<Instellingen />} />}
                {(rol === "superadmin" || (rol === "admin" && can("firmas"))) && (
                  <Route path="/firmas" element={<Firmas user={user} activeFirmaId={activeFirmaId} onKiesFirma={onKiesFirma} onFirmasChanged={laadFirmas} />} />
                )}
                {rol === "superadmin" && <Route path="/logboek" element={<Logboek />} />}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              )}
            </main>
          </div>
        </div>
      </PermContext.Provider>
      </SalaryVisibilityProvider>
    </AuthContext.Provider>
  );
}

// ── Main App ───────────────────────────────────────────
function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [firma, setFirma] = useState(null);
  const [activeFirmaId, setActiveFirmaId] = useState(() => localStorage.getItem(FIRMA_KEY) || "");
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));

  const handleLogout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch (e) {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(FIRMA_KEY);
    setAuthHeaders(null, null);
    setToken("");
    setUser(null);
    setFirma(null);
    setActiveFirmaId("");
  }, []);

  const handleLogin = useCallback((data) => {
    const firmaId = data.user.rol === "superadmin" ? (localStorage.getItem(FIRMA_KEY) || "") : data.user.firma_id;
    localStorage.setItem(TOKEN_KEY, data.token);
    if (firmaId) localStorage.setItem(FIRMA_KEY, firmaId);
    setAuthHeaders(data.token, firmaId);
    setToken(data.token);
    setUser(data.user);
    setFirma(data.firma);
    setActiveFirmaId(firmaId || "");
  }, []);

  const handleKiesFirma = useCallback((firmaId) => {
    if (!firmaId) {
      localStorage.removeItem(FIRMA_KEY);
      setAuthHeaders(localStorage.getItem(TOKEN_KEY), null);
      setActiveFirmaId("");
      setFirma(null);
      return;
    }
    localStorage.setItem(FIRMA_KEY, firmaId);
    setAuthHeaders(localStorage.getItem(TOKEN_KEY), firmaId);
    setActiveFirmaId(firmaId);
    setFirma(null);
  }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data.user);
        if (res.data.user.rol !== "superadmin") {
          const fid = res.data.user.firma_id;
          setFirma(res.data.firma);
          setActiveFirmaId(fid || "");
          if (fid) {
            localStorage.setItem(FIRMA_KEY, fid);
            setAuthHeaders(token, fid);
          }
        } else {
          // Controleer of de bewaarde firma nog bestaat
          const opgeslagen = localStorage.getItem(FIRMA_KEY);
          if (opgeslagen) {
            try {
              await axios.get(`${API}/firmas/${opgeslagen}`);
            } catch (e) {
              localStorage.removeItem(FIRMA_KEY);
              setAuthHeaders(token, null);
              setActiveFirmaId("");
            }
          }
        }
      } catch (e) {
        handleLogout();
      } finally {
        setChecking(false);
      }
    })();
  }, [token, handleLogout]);

  if (checking) {
    return <div className="min-h-screen bg-[#F4F5F7] flex items-center justify-center text-sm text-muted-foreground" data-testid="auth-checking">Laden...</div>;
  }

  const ingelogd = !!user;
  const moetFirmaKiezen = ingelogd && user.rol === "superadmin" && !activeFirmaId;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/inklokken" element={<Inklokken />} />
        <Route path="/inklokken/:filiaalId" element={<Inklokken />} />
        <Route path="/rit" element={<RitPage />} />
        <Route path="/driver/:did" element={<DriverPage />} />
        <Route
          path="/*"
          element={
            !ingelogd ? (
              <Login onLogin={handleLogin} />
            ) : moetFirmaKiezen ? (
              <FirmaKiezer onKies={handleKiesFirma} onLogout={handleLogout} />
            ) : (
              <AppLayout
                user={user}
                firma={firma}
                activeFirmaId={activeFirmaId}
                onLogout={handleLogout}
                onKiesFirma={handleKiesFirma}
              />
            )
          }
        />
      </Routes>
      <Toaster position="top-right" />
      <InstallBanner />
    </BrowserRouter>
  );
}

export default App;
